import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { renderPdfBlobIntoHost } from "src/app/core/utils/pdf-light-viewer.util";

@Component({
  selector: "app-light-pdf-preview",
  templateUrl: "./light-pdf-preview.component.html",
  styleUrls: ["./light-pdf-preview.component.scss"],
})
export class LightPdfPreviewComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  @Input() pdfBlob: Blob | null = null;
  @Input() title = "PDF preview";

  @ViewChild("pageHost", { static: true })
  pageHost!: ElementRef<HTMLElement>;

  loading = false;
  loadError = false;

  private loadGeneration = 0;
  private pendingBlob: Blob | null = null;
  private viewReady = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["pdfBlob"]) {
      this.pendingBlob = this.pdfBlob;
      void this.tryLoadPreview();
    }
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    void this.tryLoadPreview();
  }

  ngOnDestroy(): void {
    this.loadGeneration += 1;
    this.clearPages();
  }

  private async tryLoadPreview(): Promise<void> {
    if (!this.viewReady || !this.pendingBlob) {
      return;
    }
    await this.loadPreview(this.pendingBlob);
  }

  private async loadPreview(blob: Blob): Promise<void> {
    const generation = ++this.loadGeneration;
    this.clearPages();
    this.loadError = false;
    await new Promise<void>((resolve) => setTimeout(resolve, 0));
    if (generation !== this.loadGeneration) {
      return;
    }
    this.loading = true;

    try {
      await this.waitForHostWidth();
      if (generation !== this.loadGeneration) {
        return;
      }
      await renderPdfBlobIntoHost(
        this.pageHost.nativeElement,
        blob,
        this.title
      );
    } catch {
      if (generation === this.loadGeneration) {
        this.loadError = true;
      }
    } finally {
      if (generation === this.loadGeneration) {
        this.loading = false;
      }
    }
  }

  private waitForHostWidth(maxAttempts = 40): Promise<void> {
    return new Promise((resolve) => {
      let attempts = 0;
      const tick = (): void => {
        const width = this.pageHost?.nativeElement?.clientWidth ?? 0;
        if (width > 0 || attempts >= maxAttempts) {
          resolve();
          return;
        }
        attempts += 1;
        requestAnimationFrame(tick);
      };
      tick();
    });
  }

  private clearPages(): void {
    const host = this.pageHost?.nativeElement;
    if (host) {
      host.replaceChildren();
    }
  }
}
